import type { HTMLAttributes, ReactNode } from 'react';
import React, { forwardRef, useCallback, useEffect, useId, useMemo, useRef } from 'react';

import type { RenderModelDocument, RenderModelPage } from '@aline/common';
import { inToPx, mapObjectValues, pageSeparationPx, Select } from '@aline/common';
import clsx from 'clsx';
import { FixedSizeList } from 'react-window';
import type { Editor, Path } from 'slate';
import { useScrollable } from 'src/components';
import { useEditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { composeRefs } from 'src/helpers/compose-refs';
import { IS_PLAYWRIGHT } from 'src/helpers/environment';
import { useAlineMeasure } from 'src/hooks/dom-hooks';
import type { GenericKeyValMap } from 'src/types/general';
import { makeStyles } from 'tss-react/mui';

import { MobileSelectionHandles } from './MobileSelectionHandles';
import type { PageProps } from './Page';
import { Page } from './Page';
import { SelectionPopover } from './SelectionPopover';
import { useEditor, useSelection, useSlateSelector } from '../hooks';
import { useSafeRemoteSelections } from '../hooks/remote-selection';

/** The slice of react-window's list API the editor drives imperatively. */
export interface EditorScroller {
  scrollTo: (scrollOffset: number) => void;
}

export const EDITOR_TO_REACT_WINDOW = new WeakMap<Editor, EditorScroller>();
export const EDITOR_TO_DOCUMENT_ELEMENT = new WeakMap<Editor, HTMLElement>();

const useStyles = makeStyles()(() => ({
  document: {
    height: '100%',
  },
}));

interface DataProps extends Omit<PageProps, 'page' | 'pageNumber' | 'path'> {
  pages: RenderModelPage[];
  rMDocPath: Path;
}

const OuterElement = forwardRef(function OuterWindowElement(
  { className, style = {}, ...props }: HTMLAttributes<HTMLDivElement>,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const id = useId();
  const { height, ...restStyle } = style;
  const { classes, scrollableInnerRef, scrollableRef, scrollbarElements } = useScrollable({
    id,
    visibleWhenNotOverflowing: { vertical: true },
  });

  return (
    <div className={classes.root} style={{ height }}>
      <div
        ref={composeRefs(ref, scrollableRef)}
        className={clsx(className, classes.scrollable)}
        data-cy={Select.document.editor.reactWindow}
        id={id}
        style={restStyle}
        {...props}
      >
        <div ref={scrollableInnerRef} className={classes.scrollableInner}>
          {props.children}
        </div>

        <PageWidthWrapper>
          <SelectionPopover />
          <MobileSelectionHandles />
        </PageWidthWrapper>
      </div>

      {scrollbarElements}
    </div>
  );
});

interface DocumentProps {
  document: RenderModelDocument;
  path: Path;
  attributes?: GenericKeyValMap;
}

export function Document(props: DocumentProps): JSX.Element {
  const { attributes, document, path } = props;
  const { classes } = useStyles();
  const editor = useEditor();
  const selection = useSelection();
  const remoteSelections = useSafeRemoteSelections();
  const {
    reactWindow: { setScrollOffset, setWindowHeight },
  } = useEditorTabStateActions();
  const [measureDocumentRef, measurements] = useAlineMeasure<HTMLDivElement>();
  const activePageDimensions = Queries.activePageDimensions(editor, 'in');
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const documentRef = useCallback(
    (documentEl: HTMLDivElement | null) => {
      measureDocumentRef(documentEl);
      if (documentEl) EDITOR_TO_DOCUMENT_ELEMENT.set(editor, documentEl);
      else EDITOR_TO_DOCUMENT_ELEMENT.delete(editor);
    },
    [editor, measureDocumentRef]
  );

  const refCallback = useCallback(
    (reactWindow: FixedSizeList | null) => {
      if (reactWindow) EDITOR_TO_REACT_WINDOW.set(editor, reactWindow);
    },
    [editor]
  );
  const documentHeight = measurements?.height ?? 0;

  useEffect((): void => {
    setWindowHeight(documentHeight);
  }, [documentHeight, setWindowHeight]);

  /**
   * `scrollOffset` mirrors the scroller's `scrollTop`, and react-window only writes it on a
   * scroll event. Republish the DOM's value on every setup — on mount, and again each time a
   * hidden tab pane is shown — so the mirror can never describe a position the scroller does
   * not have. Selection scrolling measures the viewport from this value, so a stale one makes
   * the next click jump the page.
   */
  useEffect((): void => {
    const scrollerEl = scrollerRef.current;
    if (!scrollerEl) return;
    setScrollOffset(scrollerEl.scrollTop);
  }, [setScrollOffset]);

  const rMSelections = selection && Queries.getRMRangesFromCMRange(editor, selection).toArray();

  const expandedRemoteSelections = Queries.expandRemoteGridSelections(editor, remoteSelections);

  const rMRemoteSelections = mapObjectValues(expandedRemoteSelections ?? {}, range =>
    Queries.getRMRangesFromCMRange(editor, range).toArray()
  );

  const itemData: DataProps = useMemo(
    () => ({
      activePageDimensions,
      pages: document.children,
      rMDoc: document,
      rMDocPath: path,
      rMRemoteSelections,
      rMSelections,
    }),
    [activePageDimensions, document, path, rMRemoteSelections, rMSelections]
  );

  // Page height + 16px for spacing between pages.  Note: Changing pageSeparationPx effects resolving the selection so change w/ care.
  const windowItemSize = inToPx(activePageDimensions.height) + pageSeparationPx;

  return (
    <div ref={documentRef} data-document className={classes.document} {...attributes}>
      <FixedSizeList
        ref={refCallback}
        height={documentHeight}
        itemCount={document.children.length}
        itemData={itemData}
        itemSize={windowItemSize}
        outerElementType={OuterElement}
        outerRef={scrollerRef}
        // Show all pages in Playwright so that they appear in Percy snapshots
        overscanCount={IS_PLAYWRIGHT ? Infinity : 1}
        width="100%"
        onScroll={({ scrollOffset }) => {
          setScrollOffset(scrollOffset);
        }}
      >
        {PageWindow}
      </FixedSizeList>
    </div>
  );
}

interface PageWindowProps {
  data: DataProps;
  index: number;
  style: React.CSSProperties;
}

function PageWindow({
  data: { pages, rMDocPath, ...pageProps },
  index,
  style,
}: PageWindowProps): JSX.Element | null {
  const page = pages[index];
  const pagePath = [...rMDocPath, index];

  return (
    <div data-page-window style={style}>
      <Page key={index} page={page} pageNumber={index + 1} path={pagePath} {...pageProps} />
    </div>
  );
}

/**
 * Wrapper component that shifts its children horizontally so that they start at the left page edge.
 * Since the horizontal offset of pages is CSS-driven, we must use CSS to absolutely position
 * document-level elements such as the selection popover relative to pages.
 *
 * To prevent absolutely positioned elements from blocking mouse events, this wrapper and its
 * descendants disable pointer events by default. The EditorPopover re-enables these pointer events
 * automatically.
 */
function PageWidthWrapper({ children }: { children: ReactNode }) {
  const pageWidthPx = useSlateSelector(
    useCallback(editor => Queries.activePageDimensions(editor, 'px').width, [])
  );

  return (
    <div style={{ display: 'flex', inset: 0, pointerEvents: 'none', position: 'absolute' }}>
      <div style={{ margin: '0 auto', position: 'relative', width: pageWidthPx }}>{children}</div>
    </div>
  );
}
